import { useMemo } from 'react';
import { useTheme } from '@mui/material';

import type {
  ApiDashboardAggregate,
  CrossSurveyAggregate,
  DiagramType,
  UpstreamApiCrossSurveyBenchmarkData,
} from 'src/types/dashboard';
import { isNumber, roundOrNull } from 'src/helper/helper';

interface UseDoughnutReDataParams {
  diagram?: DiagramType;
  focalAggregate?: ApiDashboardAggregate | null;
  comparisonAggregate?: ApiDashboardAggregate | null;
  benchmarkData?: UpstreamApiCrossSurveyBenchmarkData;
  benchmarkQuestion?: string;
  cutoutRegularDoughnut?: number;
}

interface DoughnutReData {
  totalValue: number | null;
  yoyDiffValue: number | null;
  comparisonValue: number | null;
  showPercent: boolean;
  benchmarkResultForWidget: CrossSurveyAggregate | undefined;
  benchmarkValue: number | null;
  benchmarkColor: string;
  benchmarkDataSet: BenchmarkDataSet;
}

export type BenchmarkDataSet = {
  borderColor: string[];
  backgroundColor: string[];
  borderWidth: number;
  cutout: string;
  data: number[];
} | null;

export const useDoughnutReData = ({
  diagram,
  focalAggregate,
  comparisonAggregate,
  benchmarkData,
  benchmarkQuestion,
  cutoutRegularDoughnut = 70,
}: UseDoughnutReDataParams): DoughnutReData => {
  const theme = useTheme();

  const selectedAttributeIsMean =
    diagram?.attribute && diagram.attribute === 'mean';

  // general widget

  const totalValue = useMemo(
    () =>
      (selectedAttributeIsMean
        ? roundOrNull(focalAggregate?.mean)
        : roundOrNull(focalAggregate?.ptop2)) ?? null,
    [focalAggregate, selectedAttributeIsMean],
  );

  const yoyDiffValue = useMemo(
    () =>
      (selectedAttributeIsMean
        ? roundOrNull(focalAggregate?.mean_yoyDiff)
        : roundOrNull(focalAggregate?.ptop2_yoyDiff)) ?? null,
    [focalAggregate, selectedAttributeIsMean],
  );

  const comparisonValue = useMemo(
    () =>
      (selectedAttributeIsMean
        ? roundOrNull(comparisonAggregate?.mean)
        : roundOrNull(comparisonAggregate?.ptop2)) ?? null,
    [comparisonAggregate, selectedAttributeIsMean],
  );

  const showPercent = Boolean(diagram?.show_percent ?? 1);

  // cross survey benchmark

  const benchmarkResultForWidget = useMemo(() => {
    if (
      benchmarkQuestion &&
      benchmarkData?.results &&
      benchmarkData?.results.length > 0
    ) {
      return benchmarkData.results
        .flatMap((result) => result.aggregates)
        .find((aggregate) => aggregate.question == benchmarkQuestion);
    }
  }, [benchmarkQuestion, benchmarkData]);

  const benchmarkValue =
    (benchmarkResultForWidget && diagram?.attribute && selectedAttributeIsMean
      ? roundOrNull(benchmarkResultForWidget?.mean)
      : roundOrNull(benchmarkResultForWidget?.ptop2)) ?? null;

  const benchmarkColor = diagram?.benchmark_color ?? theme.colors.black;

  const benchmarkDataSet: BenchmarkDataSet = useMemo(() => {
    if (benchmarkValue === null || !isNumber(benchmarkValue)) return null;

    const data = [benchmarkValue, 100 - benchmarkValue];
    const chartColors = [benchmarkColor, 'transparent'];
    // use cutout of outer doughnut with offset
    const cutout = isNumber(cutoutRegularDoughnut)
      ? Number(0.9 * cutoutRegularDoughnut)
      : 63;

    return {
      borderColor: chartColors,
      backgroundColor: chartColors,
      borderWidth: 1,
      cutout: `${cutout}%`,
      data: data,
    };
  }, [benchmarkValue, benchmarkColor, cutoutRegularDoughnut]);

  return {
    totalValue,
    yoyDiffValue,
    comparisonValue,
    showPercent,
    benchmarkResultForWidget,
    benchmarkValue,
    benchmarkColor,
    benchmarkDataSet,
  };
};
